import { sourceAudioDuration } from './sourceAudioDuration';

// Musical loops are already cut to their native bar lengths. The audition
// window is unrelated. Tagged lengths (including fractional bars) win.
// Untagged full-bar loops may carry up to 0.6s of export/codec padding;
// that padding never becomes part of the repeat period. FX keep their duration.
const positive = value => Number.isFinite(Number(value)) && Number(value) > 0;
const isFx = group => ['sound fx', 'fx'].includes(String(group || '').toLowerCase());
const timingBpm = track => positive(track.bpm) ? Number(track.bpm)
  : isFx(track.instrumentGroup) ? (positive(track.sessionBpm) ? Number(track.sessionBpm) : 90) : null;
export const sourceLoopBars = track => {
  if (positive(track.bars)) return Number(track.bars);
  if (!positive(track.durationSeconds) || !positive(timingBpm(track))) return null;
  const duration = Number(track.durationSeconds), secondsPerBar = 240 / timingBpm(track);
  const rawBars = duration / secondsPerBar;
  if (isFx(track.instrumentGroup)) return rawBars;
  // Shorter musical loops can be tagged explicitly; exact quarter-bar cuts
  // also survive duration-only imports, allowing codec timing tolerance.
  const bars = rawBars < 1 ? Math.round(rawBars * 4) / 4 : Math.round(rawBars);
  if (!(bars > 0)) return null;
  const remainder = duration - bars * secondsPerBar;
  const maxTail = bars < 1 ? 0.03 : 0.6;
  return remainder >= -0.03 && remainder <= maxTail ? bars : null;
};
const groups = { Melodies: 'melody', Drums: 'drums', Textures: 'texture', 'Sound FX': 'fx', 'Generated Bass': 'bass' };
export const previewHandoffTrack = track => ({
  id: track.loopId,
  name: track.title,
  url: track.previewUrl,
  bpm: timingBpm(track),
  key: track.musicalKey || '-',
  instrument_group: groups[track.instrumentGroup] || 'unknown',
  bars: sourceLoopBars(track),
  energy: track.energy || null,
  style_tags: track.styleTags || [],
  pack_code: track.packCode || null,
});

// Send waits for source metadata instead of rejecting a freshly added loop while
// the provider's background metadata read is still pending. Never use session BPM
// for timed loops: the receiver is responsible for stretching to the song tempo.
export async function preparePreviewHandoffTracks(tracks, sessionBpm) {
  return Promise.all(tracks.map(async track => {
    let source = { ...track, sessionBpm };
    if (!(sourceLoopBars(source) > 0) && !positive(source.durationSeconds) && source.previewUrl) {
      try { source.durationSeconds = await sourceAudioDuration(source.previewUrl); }
      catch { throw new Error(`Could not load the full audio length for “${track.title || 'this loop'}”. Try sending again.`); }
    }
    const payload = previewHandoffTrack(source);
    if (!(payload.bars > 0)) throw new Error(`Check the BPM and bar tags for “${track.title || 'this loop'}”; its audio length does not match those tags.`);
    return payload;
  }));
}
